import { useEffect, useState, useMemo } from "react";
import { deleteData, getData, postData, updateData } from "./services/api";
import { obtenerDocentesDisponibles } from "./utils/docentes";
import {
  cargarCatalogoDocentes,
  combinarDocentes,
  guardarCatalogoDocentes,
  obtenerDocentesDeRegistro
} from "./utils/catalogoDocentes";
import Sidebar from "./components/Sidebar";
import ModalDetalle from "./components/ModalDetalle";
import ModalFormulario from "./components/ModalFormulario";
import ModalDocente from "./components/ModalDocente";
import { theme } from "./styles/theme";

import TabsBar from "./components/TabsBar";
import TablaRegistros from "./components/TablaRegistros";
import Avances from "./components/Avances";
import TablasDocentes from "./components/TablasDocentes";
import TemariosEstudiantes from "./components/TemariosEstudiantes";

const TABS_INVITADO = ["tabla", "stats2"];

function App() {
  const [data, setData] = useState([]);
  const [tabActiva, setTabActiva] = useState("tabla");
  const [docenteSeleccionado, setDocenteSeleccionado] = useState(null);
  const [anioAvancesActivo, setAnioAvancesActivo] = useState("2026");
  const [mostrarForm, setMostrarForm] = useState(false);
  const [mostrarModalDocente, setMostrarModalDocente] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cargandoDatos, setCargandoDatos] = useState(true);
  const [errorCargaDatos, setErrorCargaDatos] = useState("");
  const [catalogoDocentes, setCatalogoDocentes] = useState([]);
  const [registroEditando, setRegistroEditando] = useState(null);
  const [enlaceCopiado, setEnlaceCopiado] = useState(false);
  const soloLectura = useMemo(() => {
    const parametros = new URLSearchParams(window.location.search);
    return parametros.get("invitado") === "1";
  }, []);

  const refrescarDatos = async () => {
    setCargandoDatos(true);
    setErrorCargaDatos("");

    try {
      const datos = await getData();
      setData(datos);
    } catch (error) {
      console.error("Error al cargar datos desde Google Sheets:", error);
      setErrorCargaDatos(error.message || "No se pudo conectar con Google Sheets.");
    } finally {
      setCargandoDatos(false);
    }
  };

  useEffect(() => {
    refrescarDatos();
  }, []);

  useEffect(() => {
    if (soloLectura && !TABS_INVITADO.includes(tabActiva)) {
      setTabActiva("tabla");
    }
  }, [soloLectura, tabActiva]);

  useEffect(() => {
    const catalogoGuardado = cargarCatalogoDocentes();

    if (catalogoGuardado.length > 0) {
      setCatalogoDocentes(catalogoGuardado);
    }
  }, []);

  const docentesDisponibles = useMemo(() => {
    return combinarDocentes(catalogoDocentes);
  }, [catalogoDocentes]);

  useEffect(() => {
    if (data.length === 0) {
      return;
    }

    setCatalogoDocentes((prevCatalogo) => {
      const baseSheet = obtenerDocentesDisponibles(data);
      const combinado = combinarDocentes(prevCatalogo, baseSheet);

      if (combinado.length === prevCatalogo.length) {
        return prevCatalogo;
      }

      guardarCatalogoDocentes(combinado);
      return combinado;
    });
  }, [data]);

  const actualizarCatalogo = (nuevosDocentes) => {
    setCatalogoDocentes((prevCatalogo) => {
      const combinado = guardarCatalogoDocentes(combinarDocentes(prevCatalogo, nuevosDocentes));
      return combinado;
    });
  };

  const handleGuardarRegistro = async (nuevoRegistro) => {
    if (soloLectura) {
      return;
    }

    setGuardando(true);

    try {
      if (registroEditando) {
        if (!registroEditando.registro?._rowNumber) {
          throw new Error(
            "Este registro no tiene identificador de fila para actualizar. Debes publicar la version nueva del Apps Script con soporte de edicion."
          );
        }

        await updateData({
          ...nuevoRegistro,
          _rowNumber: registroEditando.registro._rowNumber
        });
        setData((prevData) =>
          prevData.map((registro, index) => (index === registroEditando.index ? nuevoRegistro : registro))
        );
      } else {
        const estudiantesLote = Array.isArray(nuevoRegistro._bulkStudents)
          ? nuevoRegistro._bulkStudents.filter(Boolean)
          : null;

        if (estudiantesLote && estudiantesLote.length > 1) {
          const registrosAGuardar = estudiantesLote.map((estudianteItem) => ({
            ...nuevoRegistro,
            estudiante: estudianteItem.estudiante,
            cedula: estudianteItem.cedula,
            cohorte: estudianteItem.cohorte,
            fecha_inicio: estudianteItem.fecha_inicio ?? nuevoRegistro.fecha_inicio,
            fecha_fin: estudianteItem.fecha_fin ?? nuevoRegistro.fecha_fin
          }));

          for (const registro of registrosAGuardar) {
            await postData(registro);
          }

          setData((prevData) => [
            ...prevData,
            ...registrosAGuardar
          ]);
        } else {
          await postData(nuevoRegistro);
          setData((prevData) => [...prevData, nuevoRegistro]);
        }
      }

      const registrosCatalogo = Array.isArray(nuevoRegistro._bulkStudents) && nuevoRegistro._bulkStudents.length > 1
        ? nuevoRegistro._bulkStudents.map((estudianteItem) => ({
            ...nuevoRegistro,
            estudiante: estudianteItem.estudiante,
            cedula: estudianteItem.cedula,
            cohorte: estudianteItem.cohorte,
            fecha_inicio: estudianteItem.fecha_inicio ?? nuevoRegistro.fecha_inicio,
            fecha_fin: estudianteItem.fecha_fin ?? nuevoRegistro.fecha_fin
          }))
        : [nuevoRegistro];

      actualizarCatalogo(registrosCatalogo.flatMap((registro) => obtenerDocentesDeRegistro(registro)));
      setRegistroEditando(null);
      setMostrarForm(false);
      await refrescarDatos();
    } catch (error) {
      console.error("Error al guardar en Google Sheets:", error);
      window.alert(`No se pudo guardar en Google Sheets.\n\n${error.message}`);
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardarDocente = (nombreDocente) => {
    if (soloLectura) {
      return;
    }

    actualizarCatalogo([nombreDocente]);
    setMostrarModalDocente(false);
  };

  const handleNuevoRegistro = () => {
    if (soloLectura) {
      return;
    }

    setRegistroEditando(null);
    setMostrarForm(true);
  };

  const handleEditarRegistro = (registro, index) => {
    if (soloLectura) {
      return;
    }

    setRegistroEditando({ registro, index });
    setMostrarForm(true);
  };

  const handleEliminarRegistro = async (registro, index) => {
    if (soloLectura) {
      return;
    }

    const confirmado = window.confirm(
      `¿Deseas eliminar el registro de ${registro?.estudiante || "este estudiante"}?\n\nEsta accion eliminara la fila en Google Sheets.`
    );

    if (!confirmado) {
      return;
    }

    if (!registro?._rowNumber) {
      window.alert(
        "Este registro no tiene identificador de fila para eliminar. Debes publicar la version nueva del Apps Script con soporte de eliminacion."
      );
      return;
    }

    setGuardando(true);

    try {
      await deleteData({ _rowNumber: registro._rowNumber });
      setData((prevData) => prevData.filter((_, itemIndex) => itemIndex !== index));
      await refrescarDatos();
    } catch (error) {
      console.error("Error al eliminar en Google Sheets:", error);
      window.alert(`No se pudo eliminar el registro en Google Sheets.\n\n${error.message}`);
    } finally {
      setGuardando(false);
    }
  };

  const handleCerrarFormulario = () => {
    setMostrarForm(false);
    setRegistroEditando(null);
  };

  const copiarEnlaceInvitado = async () => {
    const enlace = new URL(window.location.href);
    enlace.searchParams.set("invitado", "1");

    try {
      await navigator.clipboard.writeText(enlace.toString());
    } catch {
      const temporal = document.createElement("textarea");
      temporal.value = enlace.toString();
      temporal.setAttribute("readonly", "");
      temporal.style.position = "fixed";
      temporal.style.opacity = "0";
      document.body.appendChild(temporal);
      temporal.select();
      document.execCommand("copy");
      document.body.removeChild(temporal);
    }

    setEnlaceCopiado(true);
    window.setTimeout(() => setEnlaceCopiado(false), 2200);
  };

  return (
    <div style={layout.container}>

      {/* 🔵 HEADER */}
      <div style={layout.header}>
        REGISTROS DE TUTORES Y EVALUADORES
      </div>

      {/* 🧱 MAIN */}
      <div style={layout.main}>

        {/* ✅ SIDEBAR */}
        <Sidebar
          onAgregar={handleNuevoRegistro}
          onAgregarDocente={() => !soloLectura && setMostrarModalDocente(true)}
          onCopiarEnlaceInvitado={copiarEnlaceInvitado}
          enlaceCopiado={enlaceCopiado}
          soloLectura={soloLectura}
        />

        {/* ✅ CONTENIDO */}
        <div style={layout.content}>
          {errorCargaDatos && (
            <div style={layout.errorPanel}>
              <div>
                <strong>No se pudieron cargar los datos de Google Sheets.</strong>
                <p style={layout.errorText}>{errorCargaDatos}</p>
              </div>
              <button type="button" onClick={refrescarDatos} style={layout.retryButton}>
                Reintentar
              </button>
            </div>
          )}

          {!errorCargaDatos && (
            <div style={layout.statusLine}>
              {cargandoDatos ? "Cargando datos de Google Sheets..." : `${data.length} registros cargados`}
            </div>
          )}

          <TabsBar
            tabActiva={tabActiva}
            setTabActiva={setTabActiva}
            tabsPermitidos={soloLectura ? TABS_INVITADO : undefined}
          />

          {tabActiva === "tabla" && (
            <TablaRegistros
              data={data}
              onEdit={handleEditarRegistro}
              onDelete={handleEliminarRegistro}
              soloLectura={soloLectura}
            />
          )}
          {tabActiva === "stats2" && (
            <Avances
              data={data}
              anioActivo={anioAvancesActivo}
              onChangeAnio={setAnioAvancesActivo}
              onSelect={setDocenteSeleccionado}
            />
          )}
          {!soloLectura && tabActiva === "tablas" && <TablasDocentes data={data} />}
          {!soloLectura && tabActiva === "temarios" && <TemariosEstudiantes data={data} />}

        </div>

      </div>

      {/* MODAL */}
      {docenteSeleccionado && (
        <ModalDetalle
          docente={docenteSeleccionado}
          data={data}
          anioSeleccionado={anioAvancesActivo}
          onClose={() => setDocenteSeleccionado(null)}
        />
      )}

      {mostrarForm && (
        <ModalFormulario
          onClose={handleCerrarFormulario}
          onSubmit={handleGuardarRegistro}
          isSubmitting={guardando}
          docentesDisponibles={docentesDisponibles}
          initialData={registroEditando?.registro || null}
          modo={registroEditando ? "editar" : "crear"}
        />
      )}

      {mostrarModalDocente && (
        <ModalDocente
          onClose={() => setMostrarModalDocente(false)}
          onSave={handleGuardarDocente}
          docentesDisponibles={docentesDisponibles}
        />
      )}

    </div>
  );
}

export default App;
const layout = {
  container: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    background: theme.colors.appBg
  },

  header: {
    height: "60px",
    background: `linear-gradient(90deg, ${theme.colors.headerStart}, ${theme.colors.headerEnd})`,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    letterSpacing: "0.04em",
    boxShadow: "0 10px 30px rgba(37, 99, 235, 0.18)"
  },

  main: {
    display: "flex",
    flex: 1
  },

  content: {
    flex: 1,
    padding: "20px",
    overflowY: "auto",
    background: "linear-gradient(180deg, #f9fafb 0%, #edf1f5 100%)"
  },

  statusLine: {
    marginBottom: "12px",
    color: theme.colors.mutedText,
    fontSize: "13px",
    fontWeight: 700
  },

  errorPanel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "14px",
    padding: "14px 16px",
    borderRadius: "14px",
    border: "1px solid #f5c2c7",
    background: "#fff5f5",
    color: "#8a1f17",
    boxShadow: "0 10px 24px rgba(138, 31, 23, 0.08)"
  },

  errorText: {
    margin: "6px 0 0",
    color: "#9f2a22"
  },

  retryButton: {
    border: "1px solid #d7a0a4",
    borderRadius: "10px",
    background: "#ffffff",
    color: "#8a1f17",
    padding: "9px 12px",
    cursor: "pointer",
    fontWeight: 800,
    whiteSpace: "nowrap"
  }
};
